import type { ZudokuConfig } from "zudoku";
import { AuthGate } from "./src/auth/AuthGate";
import { LoggedOutPage } from "./src/auth/LoggedOutPage";
import { PUBLIC_ROUTES } from "./src/auth/publicRoutes";
import { LandingPage } from "./src/landing/LandingPage";
import { ThemeModeSwitch } from "./src/theme/ThemeModeSwitch";
import "./styles.css";

const config: ZudokuConfig = {
  site: {
    title: "Finarkein Docs",
    showPoweredBy: false,
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "Finarkein",
      width: 116,
      height: 28,
    },
  },
  metadata: {
    favicon: "/favicon.png",
    // Zudoku defaults to "%s - Zudoku".
    title: "%s",
    defaultTitle: "Finarkein Docs",
  },
  theme: {
    fonts: {
      // Finarkein's Adobe Fonts kit (same as finarkein.com); falls back to system sans.
      sans: {
        url: "https://use.typekit.net/pcf8xuu.css",
        fontFamily:
          '"forma-djr-text", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      },
      mono: {
        url: "https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&display=swap",
        fontFamily: '"DM Mono", ui-monospace, monospace',
      },
    },
    light: {
      background: "#FFFCF5",
      foreground: "#132318",
      card: "#FFFCF5",
      cardForeground: "#132318",
      popover: "#FFFCF5",
      popoverForeground: "#132318",
      primary: "#132318",
      primaryForeground: "#E1FF76",
      secondary: "#F2EEE5",
      secondaryForeground: "#132318",
      muted: "#F2EEE5",
      mutedForeground: "#6B6A62",
      accent: "#E1FF76",
      accentForeground: "#132318",
      destructive: "#D9383A",
      destructiveForeground: "#FFFFFF",
      border: "#E4E1DA",
      input: "#E4E1DA",
      ring: "#759534",
      radius: "0.375rem",
    },
    dark: {
      background: "#132318",
      foreground: "#FFFCF5",
      card: "#183021",
      cardForeground: "#FFFCF5",
      popover: "#183021",
      popoverForeground: "#FFFCF5",
      primary: "#E1FF76",
      primaryForeground: "#132318",
      secondary: "#233B2A",
      secondaryForeground: "#FFFCF5",
      muted: "#233B2A",
      mutedForeground: "#A9ADA0",
      accent: "#233B2A",
      accentForeground: "#E1FF76",
      destructive: "#E5595B",
      destructiveForeground: "#FFFFFF",
      border: "#2C4733",
      input: "#2C4733",
      ring: "#E1FF76",
      radius: "0.375rem",
    },
  },
  authentication: {
    type: "openid",
    clientId: "frontend-docs",
    issuer: "https://id.finarkein.com/auth/realms/fin-dev",
    scopes: ["openid", "profile", "email"],
    redirectToAfterSignOut: "/logged-out",
    disableSignUp: true,
  },
  // v1: client-side gate only. Content is still present in the public static
  // bundle; see README ("Access control") for the follow-up.
  // First match wins, so the public routes come before the catch-all.
  protectedRoutes: {
    ...Object.fromEntries(PUBLIC_ROUTES.map((route) => [route, () => true])),
    "/*": ({ auth, reasonCode }) =>
      auth.isAuthenticated ? true : reasonCode.UNAUTHORIZED,
  },
  header: {
    // Replaced by ThemeModeSwitch, which adds a "follow the system" option.
    themeSwitcher: { enabled: false },
  },
  slots: {
    // Splash + redirect to Keycloak for visitors without a session.
    "layout-before-head": AuthGate,
    "head-navigation-end": ThemeModeSwitch,
    "mobile-top-bar-end": ThemeModeSwitch,
  },
  // Top tabs: Home, Platform (every product, grouped into finarkein.com's four
  // layers), Solutions (by role), API reference, Resources. Product pages keep
  // their URLs; role pages are reading paths that link into them.
  navigation: [
    {
      type: "custom-page",
      path: "/",
      label: "Home",
      element: <LandingPage />,
    },
    {
      type: "category",
      label: "Platform",
      items: [
        { type: "doc", file: "platform/index", path: "/platform", label: "Finarkein Platform", icon: "layout-grid" },
        { type: "section", label: "Data collection" },
        {
          type: "category",
          label: "Multi-AA Gateway",
          items: [
            { type: "doc", file: "mag/overview", label: "Overview" },
            { type: "doc", file: "mag/how-it-works", label: "How it works" },
            {
              type: "category",
              label: "For LSPs",
              items: [
                { type: "doc", file: "mag/lsp/overview", label: "Explainer" },
                { type: "doc", file: "mag/lsp/integration-guide", label: "Integration guide" },
                { type: "doc", file: "mag/lsp/reference", label: "Reference" },
                { type: "link", to: "/mag/lsp-api", label: "API reference" },
              ],
            },
            { type: "doc", file: "mag/lender/overview", label: "For lenders" },
            { type: "doc", file: "mag/insurer/overview", label: "For insurers" },
          ],
        },
        {
          type: "category",
          label: "FIP Gateway",
          items: [
            { type: "doc", file: "fip-gateway/overview", label: "Overview" },
            { type: "doc", file: "fip-gateway/how-it-works", label: "How it works" },
            { type: "doc", file: "fip-gateway/onboarding", label: "Onboarding" },
          ],
        },
        { type: "doc", file: "doc-parser/overview", label: "Doc Parser" },
        { type: "doc", file: "digital-itr/overview", label: "Digital ITR" },
        { type: "section", label: "Data intelligence" },
        {
          type: "category",
          label: "Portfos",
          items: [
            { type: "doc", file: "portfos/overview", label: "Overview" },
            { type: "doc", file: "portfos/how-it-works", label: "How it works" },
          ],
        },
        { type: "section", label: "Autonomous actions" },
        { type: "doc", file: "openlink/overview", label: "OpenLink" },
      ],
    },
    {
      type: "category",
      label: "Solutions",
      items: [
        { type: "doc", file: "solutions/index", path: "/solutions", label: "By role" },
        { type: "section", label: "Financial institutions" },
        { type: "doc", file: "solutions/lenders", label: "Lenders", icon: "landmark" },
        { type: "doc", file: "solutions/banks", label: "Banks", icon: "building-2" },
        { type: "doc", file: "solutions/insurers", label: "Insurers", icon: "shield-check" },
        { type: "doc", file: "solutions/wealth", label: "Wealth managers and AMCs", icon: "chart-pie" },
        { type: "section", label: "Partners and platforms" },
        { type: "doc", file: "solutions/lsps", label: "Lending service providers", icon: "handshake" },
        { type: "doc", file: "solutions/digital-platforms", label: "Digital platforms", icon: "smartphone" },
        { type: "doc", file: "solutions/data-providers", label: "Data providers", icon: "database" },
      ],
    },
    {
      type: "category",
      label: "API reference",
      // Lands on an index of every product's API, not on one product's spec.
      // Add each newly published spec under its platform layer.
      items: [
        { type: "doc", file: "api-reference", path: "/api-reference", label: "All APIs", icon: "layout-grid" },
        { type: "section", label: "Data collection" },
        { type: "link", to: "/mag/lsp-api", label: "Multi-AA Gateway for LSPs" },
      ],
    },
    {
      type: "category",
      label: "Resources",
      items: [
        { type: "doc", file: "resources/getting-access", label: "Getting access" },
        { type: "doc", file: "resources/glossary", label: "Glossary" },
      ],
    },
    {
      type: "custom-page",
      path: "/logged-out",
      element: <LoggedOutPage />,
      display: "hide",
    },
  ],
  redirects: [
    { from: "/introduction", to: "/" },
    { from: "/products", to: "/platform" },
    { from: "/platform/getting-access", to: "/resources/getting-access" },
    { from: "/fiu-gateway-for-lsps", to: "/mag/lsp/overview" },
    { from: "/fiu-gateway-integration-guide", to: "/mag/lsp/integration-guide" },
    { from: "/fiu-gateway-reference", to: "/mag/lsp/reference" },
    { from: "/api", to: "/mag/lsp-api" },
  ],
  apis: [
    {
      type: "file",
      input: "./apis/lsp-apis.yaml",
      path: "/mag/lsp-api",
    },
  ],
};

export default config;
